import {
  getSmartRecoNutritionalAssessment,
  getSmartRecoNutritionalAssessmentError,
  getSmartRecoNutritionalAssessmentSuccess,
  goToRationing,
  resetSmartRecoProductRecommendationError,
  setSmartRecoNotRecommendedProductCostPerDay,
  setSmartRecoProductsCostPerDay,
  setSmartRecoTreat,
} from './../actions/smart-reco-recommendation.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Consultation,
  NutritionRecommendation,
  Patient,
  PetInfo,
  Product,
  ProductRecommendationErrorTypes,
  RenalDetectRecommendation,
  SignalRecommendationTechnicalStatus,
  SmartRecoApiBody,
} from '@app/core/models';
import { ApiManagerService, EmailService, NutritionService, ProductService } from '@app/core/services';
import { continueToProducts, GTMService, individualisEligibilityData, ModuleNameEnum } from '@app/core/services/tracking';
import { CombineProductsPopinComponent } from '@app/shared/components';
import {
  ActionsEnum,
  Constants,
  formatNotRecommendedProductCoverageAndReasons,
  getProductRecommendationErrorType,
  getProgramFromPetInfoAndProducts,
  Helper,
  ProductPillar,
  ProductType,
  Programs,
  MeasurementCodeType,
  ReproductionStatusCode,
} from '@app/shared/utils';
import { buildSignalRecommendationBodyEmail } from '@app/shared/utils/static-helpers/emails-helper';
import { filterCatalogProducts, isSolProductInEuropeanMarket } from '@app/shared/utils/static-helpers/product-filters-helper';
import { AppState } from '@app/store';
import {
  selectLastConsultation,
  selectConsultationPatient,
  selectConsultationRenalDetectRecommendation,
} from '@app/store/consultation/consultation.selectors';
import { selectBreeds, selectToolInfo, setAlert, toggleLoader } from '@app/store/core';
import {
  selectClinic,
  selectCurrentBigMeasurementUnit,
  selectCurrentClinicCountry,
  selectCurrentSystemPreferenceCode,
  selectIsIndividualisEnabled,
  selectIsSolProductsInVetEnable,
  selectMarket,
  selectVet,
} from '@app/store/vet';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RCAlertType } from '@rc/ui';
import { combineLatest, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  getSmartRecoCatalogProducts,
  getSmartRecoCatalogProductsFail,
  getSmartRecoCatalogProductsSuccess,
  getSmartRecoNotRecommendedProduct,
  getSmartRecoNotRecommendedProductSuccess,
  getSmartRecoRecommendation,
  getSmartRecoRecommendationSuccess,
  reportSmartRecoProductRecommendationIssue,
  setSmartRecoDryProduct,
  setSmartRecoProductRecommendationErrorType,
  setSmartRecoProductRecommendationSessionIdError,
  setSmartRecoWetProduct,
} from '../actions/smart-reco-recommendation.actions';
import {
  selectRecoHasIndividualis,
  selectRecoProductRecommendationRetryCount,
  selectRecoProductRecommendationSessionIdError,
} from '../selectors/smart-reco-recommendation.selectors';
import { selectSmartRecoPetSummary, selectStep } from '../selectors/smart-reco-steps.selectors';
import { NutritionalAssessment } from '@app/shared/components/nutrients-list/nutrients';
import { setSmartRecoLastStepBeforeAllowance, setSmartRecoStep } from '@app/pages/smart-reco/store/actions/smart-reco-steps.actions';
import { SmartRecoStep } from '@app/pages/smart-reco/smart-reco-step';
import { resetAllSelectedProducts, resetSelectedProduct, setSelectedProduct } from '@app/store/products';
import { selectSelectedWetProduct } from '@app/store/products/products.selectors';
import { buildRationingApiBody } from '@app/shared/utils/static-helpers/rationing-helper';
import { MatDialog } from '@angular/material/dialog';
import { ProductMapperService } from '@app/core/services/network/product/product-mapper.service';

@Injectable()
export class SmartRecoRecommendationEffects {
  /**
   * Get catalog products
   */
  getCatalogProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoCatalogProducts),
      withLatestFrom(this.store$.select(selectSmartRecoPetSummary), this.store$.select(selectIsSolProductsInVetEnable)),
      switchMap(([, petSummary, isSolProductsInVetEnable]) => {
        return this.productService
          .fetchProducts(isSolProductsInVetEnable ? [ProductPillar.VET, ProductPillar.SPTRETAIL] : [ProductPillar.VET])
          .pipe(
            map((products) =>
              filterCatalogProducts(products, {
                speciesCode: petSummary.speciesCode,
                productType: ProductType.Dry,
                solProducts: isSolProductsInVetEnable,
              }),
            ),
            switchMap((products) => [
              getSmartRecoCatalogProductsSuccess({
                catalogProducts: products,
              }),
            ]),
            catchError((err) => [getSmartRecoCatalogProductsFail({ error: err })]),
          );
      }),
    ),
  );
  /**
   * Get catalog products
   */
  getNotRecommendedProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoNotRecommendedProduct),
      withLatestFrom(
        this.store$.select(selectClinic),
        this.store$.select(selectMarket),
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectBreeds),
        this.store$.select(selectConsultationRenalDetectRecommendation),
        this.store$.select(selectCurrentSystemPreferenceCode),
      ),
      switchMap(([{ product }, clinic, market, petSummary, breeds, , currentSystemPreferenceCode]) => {
        const body = this.apiManagerService.buildApiSmartRecoInput({
          ...petSummary,
          breedObject: {
            ...petSummary.breedObject,
            sizeCategory: breeds.find((b) => b.breedCode === petSummary.breedObject.breedCode)?.sizeCategory,
          },
        });
        return this.nutritionService.getSmartRecoNotRecommendedProduct(product.id, body, clinic, market).pipe(
          switchMap((notRecommendedProduct) => {
            const product: Product | null = this.productMapperService.mapProductV2ToProduct(
              notRecommendedProduct.product,
              currentSystemPreferenceCode,
            );
            if (product) {
              return [
                getSmartRecoNotRecommendedProductSuccess({
                  notRecommendedProduct: {
                    ...formatNotRecommendedProductCoverageAndReasons(notRecommendedProduct, petSummary),
                    product,
                  },
                }),
                toggleLoader({ value: false }),
              ];
            }
            return [toggleLoader({ value: false })];
          }),
          catchError((err) => [
            setAlert({
              alert: {
                message: err,
                alertType: RCAlertType.ERROR,
                dataQa: 'vs-smart-reco-not-recommended-products-alert',
              },
            }),
            toggleLoader({ value: false }),
          ]),
        );
      }),
    ),
  );

  setDryProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoDryProduct),
      withLatestFrom(this.store$.select(selectSelectedWetProduct)),
      switchMap(([{ product, packId }, wetProduct]) => {
        return !!wetProduct
          ? [
              setSelectedProduct({
                selectedProduct: {
                  product,
                  packId,
                },
              }),
              resetSelectedProduct({ productId: wetProduct?.id }),
            ]
          : [setSelectedProduct({ selectedProduct: { product, packId } })];
      }),
    ),
  );

  /**
   * Effect to go to rationing
   */
  goToRationing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(goToRationing),
      withLatestFrom(this.store$.select(selectStep)),
      switchMap(([, step]) => [setSmartRecoLastStepBeforeAllowance({ value: step }), setSmartRecoStep({ value: SmartRecoStep.Allowance })]),
      catchError((err) => [setAlert({ alert: { message: err, alertType: RCAlertType.ERROR, dataQa: 'vs-smart-reco-navigation-alert' } })]),
    ),
  );

  /**
   * Effects to set wet product in consultation store (shared with calculator)
   */
  setWetProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoWetProduct),
      map(({ product, packId }) => setSelectedProduct({ selectedProduct: { product, packId } })),
    ),
  );

  /**
   * Effects to set treat in consultation store (shared with calculator)
   */
  setTreat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoTreat),
      map(({ product, packId }) => setSelectedProduct({ selectedProduct: { product, packId } })),
    ),
  );

  /**
   * Effects to reset selected products when a new recommendation is fetched
   */
  resetSelectedProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoRecommendation),
      switchMap(() => [resetAllSelectedProducts(), toggleLoader({ value: true })]),
    ),
  );
  /**
   * Get recommendation
   */
  getRecommendation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoRecommendation),
      withLatestFrom(
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectConsultationRenalDetectRecommendation),
        this.store$.select(selectConsultationPatient),
        this.store$.select(selectLastConsultation),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentSystemPreferenceCode),
        this.store$.select(selectVet),
        this.store$.select(selectClinic),
        this.store$.select(selectMarket),
        this.store$.select(selectBreeds),
        this.store$.select(selectRecoProductRecommendationRetryCount),
        this.store$.select(selectIsSolProductsInVetEnable),
      ),
      tap(
        ([
          ,
          petSummary,
          renalDetectRecommendation,
          consultationPatient,
          lastConsultation,
          currentBigMeasurementUnit,
          currentSystemPreferenceCode,
        ]: [
          unknown,
          PetInfo | null,
          RenalDetectRecommendation | null,
          Patient | null,
          Consultation | null,
          MeasurementCodeType,
          ...any,
        ]) => {
          this.trackingService.sendInteraction(
            continueToProducts(
              !!renalDetectRecommendation ? ModuleNameEnum.RENAL_DETECT : ModuleNameEnum.SMART_RECOMMENDATION,
              petSummary,
              consultationPatient,
              lastConsultation,
              currentBigMeasurementUnit,
            ),
          );
        },
      ),
      switchMap(
        ([
          ,
          petSummary,
          renalDetectReco,
          patient,
          lastConsultation,
          currentBigMeasurementUnit,
          currentSystemPreferenceCode,
          vet,
          clinic,
          market,
          breeds,
          retryCount,
          isSolProductsInVetEnable,
        ]) => {
          const body: SmartRecoApiBody = this.apiManagerService.buildApiSmartRecoInput({
            ...petSummary,
            breedObject: {
              ...petSummary.breedObject,
              sizeCategory: breeds.find((b) => b.breedCode === petSummary.breedObject.breedCode)?.sizeCategory,
            },
          });
          return this.nutritionService.getSmartRecoProducts(body, clinic, market, currentSystemPreferenceCode, this.LIMIT_PRODUCTS).pipe(
            switchMap((products) => {
              if (!products.length) {
                return [
                  setSmartRecoProductRecommendationErrorType({
                    value: ProductRecommendationErrorTypes.NO_RESULTS,
                  }),
                  reportSmartRecoProductRecommendationIssue({
                    body,
                    errorMessage: '** Ticket automatically generated due to lack of results **',
                    retryCount,
                    status: SignalRecommendationTechnicalStatus.KO,
                  }),
                  toggleLoader({ value: false }),
                ];
              } else {
                const filteredProducts = products.filter(
                  ({ product }) => !isSolProductInEuropeanMarket(isSolProductsInVetEnable, product, petSummary, market),
                );
                return retryCount
                  ? [
                      reportSmartRecoProductRecommendationIssue({
                        body,
                        errorMessage: '',
                        retryCount,
                        status: SignalRecommendationTechnicalStatus.OK,
                      }),
                      resetSmartRecoProductRecommendationError(),
                      getSmartRecoRecommendationSuccess({ recommendation: { products: filteredProducts } }),
                      toggleLoader({ value: false }),
                    ]
                  : [getSmartRecoRecommendationSuccess({ recommendation: { products: filteredProducts } }), toggleLoader({ value: false })];
              }
            }),
            catchError((httpError: HttpErrorResponse) => {
              return [
                setSmartRecoProductRecommendationErrorType({ value: getProductRecommendationErrorType(httpError, retryCount) }),
                reportSmartRecoProductRecommendationIssue({
                  body,
                  errorMessage: httpError?.error?.error?.message,
                  retryCount,
                  status: SignalRecommendationTechnicalStatus.KO,
                }),
                toggleLoader({ value: false }),
              ];
            }),
          );
        },
      ),
    ),
  );

  trackContinueToProducts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getSmartRecoRecommendationSuccess),
        withLatestFrom(
          this.store$
            .select(selectCurrentSystemPreferenceCode)
            .pipe(switchMap((currentSystemPreferenceCode) => this.store$.select(selectRecoHasIndividualis(currentSystemPreferenceCode)))),
        ),
        tap(([_, recoHasIndividualis]) => this.trackingService.sendInteraction(individualisEligibilityData(recoHasIndividualis))),
      ),
    { dispatch: false },
  );

  /**
   *  select first card if Individualis is not displayed after getting recommendation
   */
  setSelectedFirstCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoRecommendationSuccess),
      withLatestFrom(
        this.store$
          .select(selectCurrentSystemPreferenceCode)
          .pipe(switchMap((currentSystemPreferenceCode) => this.store$.select(selectRecoHasIndividualis(currentSystemPreferenceCode)))),
        this.store$.select(selectIsIndividualisEnabled),
      ),
      filter(([, recoHasIndividualis, isIndividualisEnabled]) => !(recoHasIndividualis && isIndividualisEnabled)),
      switchMap(([res]) => {
        return [setSmartRecoDryProduct({ product: res?.recommendation?.products[0]?.product })];
      }),
    ),
  );

  getCostsPerDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoRecommendationSuccess),
      withLatestFrom(
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectLastConsultation),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentClinicCountry),
      ),
      switchMap(([{ recommendation }, petSummary, lastConsultation, currentBigMeasurementUnit, country]) => {
        return combineLatest([
          ...recommendation.products.slice(0, 3).map((product) => {
            const currentProgram = getProgramFromPetInfoAndProducts([product?.product], petSummary);
            return this.productCostPerDay$(
              product?.product,
              petSummary,
              lastConsultation,
              currentProgram,
              currentBigMeasurementUnit,
              country,
            );
          }),
        ]).pipe(
          switchMap(([...nutritionRecommendations]) => {
            const productsCostPerDay = nutritionRecommendations.map((productNutritionRecommendation) => {
              const costsPerDay = this.extractCostsPerDay(productNutritionRecommendation);
              return this.calculateMinCostPerDay(costsPerDay);
            });
            return [setSmartRecoProductsCostPerDay({ productsCostPerDay })];
          }),
        );
      }),
    ),
  );

  private extractCostsPerDay(productNutritionRecommendation: NutritionRecommendation[]): number[] {
    return productNutritionRecommendation.map((packNutritionRecommendation) => packNutritionRecommendation?.basicDryRation?.costPerDay);
  }

  private calculateMinCostPerDay(costsPerDay: number[]): number | null {
    const productMinCostPerDay: number = Math.min(...costsPerDay);
    return productMinCostPerDay || null;
  }

  getNotRecommendedProductCostPerDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoNotRecommendedProductSuccess),
      withLatestFrom(
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectLastConsultation),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentClinicCountry),
      ),
      switchMap(([{ notRecommendedProduct }, petSummary, lastConsultation, currentBigMeasurementUnit, country]) => {
        const currentProgram = getProgramFromPetInfoAndProducts([notRecommendedProduct?.product], petSummary);
        return this.productCostPerDay$(
          notRecommendedProduct?.product,
          petSummary,
          lastConsultation,
          currentProgram,
          currentBigMeasurementUnit,
          country,
        ).pipe(
          switchMap(([...nutritionRecommendation]) => {
            const costPerDay = Math.min(
              nutritionRecommendation.map((productNutrtionRecommendation) => productNutrtionRecommendation?.basicDryRation?.costPerDay)[0],
            );
            return [setSmartRecoNotRecommendedProductCostPerDay({ costPerDay: !!costPerDay && costPerDay > 0 ? costPerDay : null })];
          }),
        );
      }),
    ),
  );

  /**
   * Get recommendation
   */
  getNutritionalAssessment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSmartRecoNutritionalAssessment),
      withLatestFrom(
        this.store$.select(selectVet),
        this.store$.select(selectClinic),
        this.store$.select(selectMarket),
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectBreeds),
        this.store$.select(selectConsultationRenalDetectRecommendation),
      ),
      switchMap(([, , clinic, market, petSummary, breeds]) => {
        const body = this.apiManagerService.buildApiSmartRecoInput({
          ...petSummary,
          breedObject: {
            ...petSummary.breedObject,
            sizeCategory: breeds.find((b) => b.breedCode === petSummary.breedObject.breedCode)?.sizeCategory,
          },
        });
        return this.nutritionService.getSmartRecoNutritionalAssessment(body, clinic, market).pipe(
          switchMap((nutritionalAssessment: NutritionalAssessment) => {
            return [getSmartRecoNutritionalAssessmentSuccess({ nutritionalAssessment })];
          }),
          catchError(() => {
            return [getSmartRecoNutritionalAssessmentError()];
          }),
        );
      }),
    ),
  );

  /**
   * Effects to reset selected products when a new recommendation is fetched
   */
  reportSmartRecoProductRecommendationIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportSmartRecoProductRecommendationIssue),
      withLatestFrom(
        this.store$.select(selectToolInfo),
        this.store$.select(selectVet),
        this.store$.select(selectClinic),
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectRecoProductRecommendationSessionIdError),
        this.store$.select(selectCurrentBigMeasurementUnit),
      ),
      switchMap(
        ([
          { body, errorMessage, retryCount, status },
          { tool_flow_id },
          vet,
          clinic,
          petSummary,
          existingSessionId,
          currentBigMeasurementUnit,
        ]) => {
          const currentSessionId = existingSessionId || Helper.randomId();

          return this.emailService
            .signalRecommendationEmail({
              countryCode: clinic.companyAddress.country,
              locale: Constants.DEFAULT,
              data: buildSignalRecommendationBodyEmail({
                clinic,
                vet,
                srApiBody: body,
                petInfo: petSummary,
                message: '',
                tool_flow_id,
                technicalInformation: {
                  errorMessage,
                  retryCount,
                  sessionId: currentSessionId,
                  status,
                },
                currentBigMeasurementUnit,
              }),
            })
            .pipe(
              switchMap(() => (existingSessionId ? [] : [setSmartRecoProductRecommendationSessionIdError({ value: currentSessionId })])),
            );
        },
      ),
    ),
  );

  /**
   * We retrieve 10 products to increase chances to have 3 left after filters
   */
  private readonly LIMIT_PRODUCTS = 10;

  private productCostPerDay$(
    product: Product,
    petInfos: PetInfo,
    lastConsultation: Consultation,
    currentProgram: Programs,
    currentBigMeasurementUnit: MeasurementCodeType,
    country: string,
  ): Observable<NutritionRecommendation[]> {
    const body = (packId) =>
      buildRationingApiBody({
        currentProgram,
        products: [product],
        selectedPackIds: [packId],
        currentRecommendation: null,
        currentConsultationVisitDate: null,
        lastConsultation,
        petInfo: petInfos,
        manualWetRation: null,
        manualTreatsRation: null,
        allocation: null,
        followUpAction: null,
        currentBigMeasurementUnit,
      });
    return combineLatest([
      ...product.packages.map((pack) => {
        return this.nutritionService
          .createRecommendation(
            currentProgram,
            body(pack.sCode),
            [product.id],
            [pack.ean],
            [product.mainItemId],
            !!body(pack.sCode)?.dryProduct?.density,
            country,
            Helper.lifestageOld(petInfos?.lifestage),
          )
          .pipe(
            switchMap((rationRecommendation) => {
              return combineLatest([rationRecommendation]).pipe(
                map(() =>
                  petInfos.reproductionStatus === ReproductionStatusCode.Gestation && petInfos.pregnancyWeeks > 0
                    ? rationRecommendation[petInfos.pregnancyWeeks - 1]
                    : rationRecommendation[0],
                ),
              );
            }),
          );
      }),
    ]);
  }

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private nutritionService: NutritionService,
    private apiManagerService: ApiManagerService,
    private emailService: EmailService,
    private productService: ProductService,
    private dialog: MatDialog,
    private trackingService: GTMService,
    private readonly productMapperService: ProductMapperService,
  ) {}

  /**
   * Display a popin for user confirmation to select a wet product
   */
  private confirmAddWetProductPopin(wetProducts: Product[]): Observable<boolean> {
    return this.dialog
      .open<CombineProductsPopinComponent, { wet_count_product: number } | undefined>(CombineProductsPopinComponent, {
        panelClass: 'regular',
        data: { wet_count_product: wetProducts.length },
      })
      .beforeClosed()
      .pipe(
        map((response: { button: ActionsEnum }) => {
          return response?.button === ActionsEnum.yes;
        }),
      );
  }
}
